import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Avatar from '~/components/Avatar';
import Background from '~/components/Background';
import Button from '~/components/Button';
import Cardbox from '~/components/Cardbox';
import CardboxWrapper from '~/components/CardboxWrapper';
import Divider from '~/components/Divider';
import Gap from '~/components/Gap';
import GoogleLoginButton from '~/components/GoogleLoginButton';
import PasswordInput from '~/components/PasswordInput';
import StyledAnchor from '~/components/StyledAnchor';
import StyledInput from '~/components/StyledInput';
import Text from '~/components/Text';
import TextButton from '~/components/TextButton';
import { PRIVACY_POLICY_URL, TERMS_URL } from '~/constants/app';
import { LOGIN_ROUTE } from '~/constants/routes';
import * as Styles from './styles';
import useSignup from './useSignup';
import { useIsDesktop } from '~/hooks/useIsDesktop';

const Signup = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const handleLoginRedirect = () => {
    navigate(LOGIN_ROUTE);
  };

  const {
    isLoading,
    isSubmitted,
    email,
    setEmail,
    password,
    setPassword,
    handleSubmit,
    resetForm,
    resendMail,
  } = useSignup();

  return (
    <Background key="signup" data-component-name="Signup">
      <CardboxWrapper className="center">
        <Styles.HalfContainer className="full-dimensions full-flex center">
          {isSubmitted ? (
            <Cardbox width="521px">
              <Text align="center" size={isDesktop ? 'lg' : 'md'} className="bold">
                Great! Now let's verify your email address.
              </Text>
              <Gap t={48} />
              <Text align="center">
                To validate your registration, an email has been sent to{' '}
                <span className="bold">{email}</span> with a link to verify your account
              </Text>
              <Gap t={48} />
              <Text align="center">
                If you have not received the email after a few minutes, please check your spam
                folder.
              </Text>
              <Gap t={48} />
              <div className="full-width center">
                <TextButton onClick={resendMail}>Resend Email</TextButton>
              </div>
              <Gap t={48} />
              <div className="full-width center">
                <TextButton variant="secondary" underline onClick={resetForm}>
                  Sign up with another email address
                </TextButton>
              </div>
            </Cardbox>
          ) : (
            <Cardbox
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSubmit();
                }
              }}
            >
              <Text align="center" size={isDesktop ? 'lg' : 'md'} className="bold">
                Create your Invoice in minutes
              </Text>
              <Gap t={56} />

              <GoogleLoginButton label="Sign up with Google" />

              <div className="full-width center">
                <Divider margin="20px 12px 20px 0" className="full-width full-flex" />
                <Text size="xs" variant="neutral" className="bold">
                  OR
                </Text>
                <Divider margin="20px 0 20px 12px" className="full-width full-flex" />
              </div>
              <Text size="xs">Email</Text>
              <Gap t={6} />
              <StyledInput
                placeholder="e.g. john@company.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                }}
                name="email"
                autoComplete="username"
              />
              <Gap t={16} />
              <Text size="xs">Password</Text>
              <Gap t={6} />
              <PasswordInput
                placeholder="**************"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                }}
                name="password"
                autoComplete="new-password"
              />
              <Gap t={24} />
              {/* <div className="flex-start cursor-pointer" onClick={() => setIsConsent(!isConsent)}>
                <input type="checkbox" className="cursor-pointer" checked={isConsent} />
                <Text size="xxs" style={{ marginLeft: '4px' }}>
                  By checking this box, I consent to receive emails and, if a phone number is
                  provided, occasional SMS or phone calls from createmyinvoice.com
                </Text>
              </div> */}
              <Gap t={24} />
              <Button
                disabled={isLoading || !email.trim() || !password.trim()}
                size="full"
                onClick={handleSubmit}
              >
                {isLoading ? <LoadingOutlined /> : 'Sign up'}
              </Button>
              <Gap t={16} />
              <Text size="xxs" align="center">
                By signing up, you acknowledge that you have read, understood and agree to
                createmyinvoice's{' '}
                <StyledAnchor variant="primary" size="xxs" href={TERMS_URL}>
                  Terms
                </StyledAnchor>{' '}
                and{' '}
                <StyledAnchor variant="primary" size="xxs" href={PRIVACY_POLICY_URL}>
                  Privacy Policy.
                </StyledAnchor>
              </Text>
              <Divider margin="32px 0" />
              <div className="full-width spread align-center">
                <Text>Already have an account?</Text>
                <Button onClick={handleLoginRedirect} variant="secondary">
                  Log in
                </Button>
              </div>
            </Cardbox>
          )}
        </Styles.HalfContainer>
        <Styles.HalfContainer className="full-dimensions full-flex center no-padding">
          <Styles.ContentContainer className="v-center">
            <Avatar imageUrl="/media/images/avatar.png" fullName="John Doe" />
            <Gap t={24} />
            <Text align="center" className="semi-bold">
              Nothing short of groundbreaking for business owners. It immediately changes how i work
              and make me 10x more productive as a business owner
            </Text>
            <Gap t={32} />
            <Text className="semi-bold">@ransegall</Text>
            <Gap t={16} />
            <Text className="semi-bold" variant="secondary">
              Founder, Flux Academy
            </Text>
          </Styles.ContentContainer>
        </Styles.HalfContainer>
      </CardboxWrapper>
    </Background>
  );
};

export default Signup;
